import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Link, router, usePage, useForm } from '@inertiajs/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import CreditsInfo from '../../Components/CreditsInfo';
import './Show.css';

const ImageComponent = memo(({ url }) => {
    const normalizedUrl = url.split('?')[0]; // Extract stable part of URL
    return <img src={url} alt="Section Image" className="img-fluid" data-stable-url={normalizedUrl} />;
});

export default function Show() {
    const { video, sections: initialSections, publicAssets, videoUrl, credits: initialCredits, movie_hash, movie_title, user_email, characters: initialCharacters, settings: initialSettings } = usePage().props;
    const { put, data, setData, processing } = useForm({
        title: video.title,
        description: video.description,
        music_id: video.music_id ? String(video.music_id) : ''
    });

    const [selectedMusicUrl, setSelectedMusicUrl] = useState('');
    const [allMediaExists, setAllMediaExists] = useState(false);
    const [buildingVideo, setBuildingVideo] = useState(false);
    const [credits, setCredits] = useState(initialCredits);
    const [sections, setSections] = useState(initialSections);
    const [characters, setCharacters] = useState(initialCharacters);
    const [settings, setSettings] = useState(initialSettings);
    const [previousAllMediaExists, setPreviousAllMediaExists] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [loadingStates, setLoadingStates] = useState(initialCharacters.map(() => false));
    const [changedDescriptions, setChangedDescriptions] = useState({});

    useEffect(() => {
        setSections(initialSections);
    }, [initialSections]);

    useEffect(() => {
        setCharacters(initialCharacters);
    }, [initialCharacters]);

    useEffect(() => {
        setSettings(initialSettings);
    }, [initialSettings]);

    useEffect(() => {
        const selectedMusic = publicAssets.find(asset => String(asset.id) === data.music_id);
        if (selectedMusic) {
            setSelectedMusicUrl(selectedMusic.temporary_url);
        } else {
            setSelectedMusicUrl('');
        }
    }, [data.music_id, publicAssets]);

    useEffect(() => {
        const mediaExists = sections.every(section => section.image && section.image.temporary_url && section.voice && section.voice.temporary_url);
        setAllMediaExists(mediaExists);
    }, [sections]);

    useEffect(() => {
        fetchCredits();
    }, []);

    useEffect(() => {
        let interval;
        
        // Keep polling if the video isn't finished OR if characters are still missing images
        const charactersMissingImages = characters.some(char => !char.asset_id);
        
        if (video.status !== "Video created" || charactersMissingImages) {
            interval = setInterval(() => {
                // Include 'characters' in the reload list to refresh character images
                router.reload({
                    only: ['video', 'videoUrl', 'sections', 'characters'] 
                });
            }, 10000); // Check every 10 seconds
        }
        return () => clearInterval(interval);
    }, [video.status, characters]); // Add characters as a dependency

    useEffect(() => {
        if (!initialLoad && previousAllMediaExists === false && allMediaExists === true) {
            toast.success('All Images and Audio have been created!');
        }
        setPreviousAllMediaExists(allMediaExists);
        if (initialLoad) {
            setInitialLoad(false);
        }
    }, [allMediaExists, previousAllMediaExists, initialLoad]);

    const fetchCredits = useCallback(async () => {
        try {
            const response = await axios.get(`/credits/${movie_hash}`);
            setCredits(response.data.credits);
        } catch (error) {
            console.error('Error fetching credits:', error);
            toast.error('Error fetching credits.');
        }
    }, [movie_hash]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        put(`/videos/${video.id}`, {
            onSuccess: () => {
                fetchCredits();
            }
        });
    }, [put, video.id, fetchCredits]);

    const handleAddSection = useCallback((order_number) => {
        router.post(`/videos/${video.id}/sections`, { order_number }, {
            onSuccess: () => {
                fetchCredits();
            }
        });
    }, [video.id, fetchCredits]);

    const handleDeleteSection = useCallback((section_id) => {
        const confirmed = confirm('Are you sure you want to delete this section?');
        if (confirmed) {
            router.delete(`/sections/${section_id}`, {
                onSuccess: () => {
                    fetchCredits();
                }
            });
        }
    }, [video.id, fetchCredits]);

    const handleGenerateMedia = useCallback(() => {
        // Check if any character is missing an image
        const missingCharacterImages = characters.filter(char => !char.asset_id);
        
        let message = "Images and Audio generation in progress. Check back in a few minutes.";
        
        if (missingCharacterImages.length > 0) {
            message = `Generating ${missingCharacterImages.length} missing character image${missingCharacterImages.length > 1 ? 's' : ''} first, then proceeding to scene media. This may take a few extra minutes.`;
        }

        const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        toast.info(message);

        // Pass a flag to the backend to handle the auto-generation
        router.post(`/videos/${video.id}/generateMedia`, {
            auto_generate_characters: missingCharacterImages.length > 0 ? 'true' : 'false'
        }, {
            headers: {
                'X-CSRF-TOKEN': csrfToken
            },
            onFinish: () => {
                fetchCredits();
            }
        });
    }, [video.id, characters, fetchCredits]);

    const handleBuildVideo = useCallback(() => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        setBuildingVideo(true);
        router.post(`/videos/${video.id}/build`, {}, {
            headers: {
                'X-CSRF-TOKEN': csrfToken
            },
            onFinish: () => {
                setBuildingVideo(false);
                fetchCredits();
            }
        });
    }, [video.id, fetchCredits]);

    // const handleGenerateMedia = useCallback(() => {
    //     toast.info("Images and Audio generation in progress. Check back in a few minutes.");
    //     router.get(`/videos/${video.id}/generateMedia`);
    // }, [video.id]);

    const handleSetAsPrimary = useCallback((e) => {
        e.preventDefault();
        router.post(`/videos/${video.id}/primary`, {}, {
            onSuccess: () => {
                toast.success('Video set as primary');
            },
            onError: () => {
                toast.error('Failed to set video as primary');
            }
        });
    }, [video.id]);

    // Determine the status of each step
    const promptsCreated = video.status !== 'creating prompts';
    const mediaGenerated = allMediaExists;
    const videoGenerated = !!videoUrl;

    const memoizedSections = useMemo(() => (
        sections.map((section, index) => (
            <div key={section.id} className="card mb-3">
                <div className="card-body">
                    <div className="row align-items-center">
                        <div className="col-md-3">
                            <Link href={`/sections/${section.id}/edit`}>
                                {section.image && section.image.temporary_url ? (
                                    <ImageComponent url={section.image.temporary_url} />
                                ) : (
                                    <p>None</p>
                                )}
                            </Link>
                        </div>
                        <div className="col-md-7">
                            <p><strong>Voice Over Text:</strong></p>
                            {section.voice && section.voice.text ? (
                                <p>{section.voice.text}</p>
                            ) : (
                                <p>None</p>
                            )}
                            <p><strong>Audio:</strong></p>
                            {section.voice && section.voice.temporary_url ? (
                                <audio controls src={section.voice.temporary_url}></audio>
                            ) : (
                                <p>None</p>
                            )}
                            <p><strong>Scene Number </strong>
                            {section.scene_number}
                            </p>
                        </div>
                        <div className="col-md-2 text-end">
                            <Link href={`/sections/${section.id}/edit`} className="btn btn-secondary mb-2 px-2">
                                Edit
                            </Link>
                            <button onClick={() => handleDeleteSection(section.id)} className="btn btn-danger mb-2 px-2 mx-2">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-footer text-center">
                    <button onClick={() => handleAddSection(section.order_number)} className="btn btn-link">
                        <i className="fa fa-plus"></i> Add Section
                    </button>
                </div>
            </div>
        ))
    ), [sections, handleAddSection, handleDeleteSection]);

    const handleDescriptionChange = (index, value, type) => {
        if (type === 'character') {
            const updatedCharacters = [...characters];
            updatedCharacters[index].description = value;
            setCharacters(updatedCharacters);
        } else {
            const updatedSettings = [...settings];
            updatedSettings[index].description = value;
            setSettings(updatedSettings);
        }

        setChangedDescriptions({
            ...changedDescriptions,
            [type]: {
                ...changedDescriptions[type],
                [index]: true
            }
        });
    };

    const handleSaveDescriptionClick = (description, index, type) => {
        const form = document.getElementById(`description-form-${type}-${index}`);
        if (form.checkValidity()) {
            saveDescription(description, index, type);
        } else {
            form.reportValidity();
        }
    };

    const saveDescription = async (description, index, type) => {
        const data = { description: description.description };
        const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

        try {
            const response = await fetch(`/descriptions/${description.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken
                },
                body: JSON.stringify(data)
            });

            if (response.ok) {
                const responseData = await response.json();
                console.log('Description saved successfully!', responseData);
                toast.success('Description saved successfully!');
                fetchCredits(); // Fetch the latest credits after saving
            } else {
                const errorData = await response.json();
                console.error('Error saving description:', errorData);
                toast.error('Failed to save description.');
            }
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
            toast.error('An error occurred while saving the description.');
        }
    };

    async function generateCharacterImage(videoDescription, index) {
        if (credits < 1) {
            toast.error('You do not have enough credits to generate character images.');
            return;
        }
        try {
            setLoadingStates(prev => {
                const newLoadingStates = [...prev];
                newLoadingStates[index] = true;
                return newLoadingStates;
            });

            // UX IMPROVEMENT: Auto-save if there are unsaved changes
            if (changedDescriptions.character && changedDescriptions.character[index]) {
                await saveDescription(videoDescription, index, 'character');
                // Reset the "changed" state for this character
                setChangedDescriptions(prev => ({
                    ...prev,
                    character: { ...prev.character, [index]: false }
                }));
            }

            toast.success('Generating character image...');

            const response = await axios.post("/generate-character-image", {
                videoDescriptionId: videoDescription.id
            });

            const responseData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

            if (responseData.error) {
                console.error('Error generating character image:', responseData.error);
                toast.error(responseData.error);
                setLoadingStates(prev => {
                    const newLoadingStates = [...prev];
                    newLoadingStates[index] = false;
                    return newLoadingStates;
                });
                return;
            }

            const updatedCharacters = [...characters];
            updatedCharacters[index] = responseData.videoDescription;
            setCharacters(updatedCharacters);

            toast.success('Character image generated successfully!');
        } catch (error) {
            console.error('Error generating character image:', error);
            toast.error('An error occurred while generating the character image.');
        } finally {
            setLoadingStates(prev => {
                const newLoadingStates = [...prev];
                newLoadingStates[index] = false;
                return newLoadingStates;
            });
        }
    }

    return (
        <div className="container mt-4">
            <ToastContainer />
            {(processing || buildingVideo) && (
                <div className="fullscreen-spinner-overlay">
                    <div className="spinner"></div>
                    <div className="spinner-text">
                        This takes on average around 2 minutes.<br />
                        You can leave and reload the page.<br />
                        It will display when done.
                    </div>
                </div>
            )}
            <CreditsInfo
                movie_title={movie_title}
                movie_hash={movie_hash}
                initialCredits={credits}
                user_email={user_email}
            />
            <Link href={`/videos/list/${movie_hash}`}>Back to Script Videos</Link>
            <br />

            <form onSubmit={handleSubmit} style={{ marginBottom: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <label htmlFor="title" style={{ display: 'block', marginBottom: '.5rem' }}>Title:</label>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="text"
            name="title"
            id="title"
            style={{ 
              flex: '1', 
              width: '50%', 
              height: '38px', // Adjust height as needed
              maxWidth: '300px', // or any specific length you prefer, for example, for a specific character length
              marginRight: '10px' 
            }}
            value={data.title}
            onChange={(e) => setData({ ...data, title: e.target.value })}
            required
          />
          <button type="submit" className="btn btn-primary" disabled={processing}>Update Title</button>
        </div>
      </div>
    </form>
            <div className="row mt-4">
                <div className="col-md-4">
                    <h4>Status</h4>
                    <ul className="list-group mb-4">
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                            {promptsCreated && sections.length > 1 ? (
                                <FontAwesomeIcon icon={faCheck} style={{ backgroundColor: 'green', color: 'white', borderRadius: '50%', padding: '5px', marginRight: '10px' }} />
                            ) : (
                                <FontAwesomeIcon icon={faTimes} spin style={{ marginRight: '10px' }} />
                            )}
                            Video Sections created
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                            {mediaGenerated && sections.length > 1 ? (
                                <FontAwesomeIcon icon={faCheck} style={{ backgroundColor: 'green', color: 'white', borderRadius: '50%', padding: '5px', marginRight: '10px' }} />
                            ) : (
                                <FontAwesomeIcon 
                                icon={faTimes} 
                                style={{ 
                                  backgroundColor: 'red', 
                                  color: 'white', 
                                  borderRadius: '50%', 
                                  padding: '5px', 
                                  marginRight: '10px' 
                                }} 
                              />
                            )}
                            Image prompts and Images generated
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                            {mediaGenerated && sections.length > 1 ? (
                                <FontAwesomeIcon icon={faCheck} style={{ backgroundColor: 'green', color: 'white', borderRadius: '50%', padding: '5px', marginRight: '10px' }} />
                            ) : (
                                <FontAwesomeIcon 
                                icon={faTimes} 
                                style={{ 
                                  backgroundColor: 'red', 
                                  color: 'white', 
                                  borderRadius: '50%', 
                                  padding: '5px', 
                                  marginRight: '10px' 
                                }} 
                              />
                            )}
                            Audio generated
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                            {videoGenerated ? (
                                <FontAwesomeIcon icon={faCheck} style={{ backgroundColor: 'green', color: 'white', borderRadius: '50%', padding: '5px', marginRight: '10px' }} />
                            ) : (
                                <FontAwesomeIcon 
                                icon={faTimes} 
                                style={{ 
                                  backgroundColor: 'red', 
                                  color: 'white', 
                                  borderRadius: '50%', 
                                  padding: '5px', 
                                  marginRight: '10px' 
                                }} 
                              />
                            )}
                            Video Built
                        </li>
                    </ul>

                    <div className='row'>
{allMediaExists && video.status !== "Video created" && sections.length > 0 && (
                        <button onClick={handleBuildVideo} className="btn btn-success me-2 my-2">
                            Build Video
                        </button>
                    )}
                    {video.status === "Video created" && (
                        <button onClick={handleBuildVideo} className="btn btn-success me-2 my-2">
                            Rebuild Video
                        </button>
                    )}

                    {videoUrl && (
                        <div className="mb-4">
                            <h2>Generated Video</h2>
                            <video controls src={videoUrl} className="img-fluid"></video>
                            <div className="mt-2">
                                <a
                                    href={`/videos/${video.id}/download`}
                                    className="btn btn-success me-2"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Download Video
                                </a>
                                <button onClick={handleSetAsPrimary} className="btn btn-secondary me-2">
                                    Make this video Primary
                                </button>
                            </div>
                        </div>
                    )}

</div>


                </div>

                <div className="col-md-8">
                    <div className="alert alert-info" role="alert" style={{ backgroundColor: '#e9ecef', padding: '20px' }}>
                        <h4 className="alert-heading">Instructions</h4>
                        <p>The video is comprised of several video sections. Each video section is an image and a voice over. <br />Image prompts have been created for each section.</p>
                        <br />
                        <ul className="numbered-list">
                            <li>Read the overall look to make sure it's what you're going for.  Change it to better align with your vision</li>
                            <li>Review all video sections and asociated scene numbers.  Modify them, add some, delete some, to present the screenplay the way you want. </li>
                            <li>Look over the Characters and make sure they are what you want.  If not, change them and regenerate the images.</li>
                            <li>Once you are happy with those, generate the prompts and media.
                                <button onClick={handleGenerateMedia} className="btn btn-success me-2 mx-2">
                                    Generate Media
                                </button>
                            </li>
                            <li>Watch the Video. Some images won't fit and you'll want to change them. Same with the voice over.</li>
                            <li>For all images you want to change, select the section and make adjustments to the prompt and regenerate the image.</li>
                            <li>Once you are happy with all the images, rebuild the video.
                                {allMediaExists && video.status !== "Video created" && sections.length > 0 && (
                                    <button onClick={handleBuildVideo} className="btn btn-success me-2 mx-2">
                                        Build Video
                                    </button>
                                )}
                                {allMediaExists && video.status === "Video created" && (
                                    <button onClick={handleBuildVideo} className="btn btn-success me-2 mx-2">
                                        Rebuild Video
                                    </button>
                                )}
                            </li>
                            <li>To attach the video to your analysis, make it the primary video.</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="row mt-4">
            <hr style={{ border: '1px solid black', margin: '10px 0' }} />
            </div>
                <div className="row mt-4">
                    <h2>Overall Look</h2>

                    {settings.map((setting, index) => (
                        <form id={`description-form-setting-${index}`} key={index} onSubmit={(e) => e.preventDefault()}>
                            <div className="card mb-3">
                                <div className="card-body">
                                <div className="alert alert-info" role="alert" style={{ backgroundColor: '#e9ecef', padding: '2px' }}>
                        <p>What's the overall look you want to use for your screenplay?  Be specific.  You can use examples from movies or TV shows.</p>
                        </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            value={setting.description}
                                            onChange={(e) => handleDescriptionChange(index, e.target.value, 'setting')}
                                            className="form-control"
                                            required
                                        ></textarea>
                                    </div>
                                    {changedDescriptions.setting && changedDescriptions.setting[index] && (
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={() => {
                                                handleSaveDescriptionClick(setting, index, 'setting');
                                                setChangedDescriptions({
                                                    ...changedDescriptions,
                                                    setting: {
                                                        ...changedDescriptions.setting,
                                                        [index]: false
                                                    }
                                                });
                                            }}
                                        >
                                            Save
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    ))}
            </div>

            <div className="row mt-4">
                <div className="col-md-8 box">
                    <h2>Video Sections</h2>
                    <div className="text-center mb-4">
                        <button onClick={() => handleAddSection(0)} className="btn btn-link">
                            <i className="fa fa-plus"></i> Add Section
                        </button>
                    </div>
                    <div className="video-sections">
                        {memoizedSections}
                    </div>

                </div>


                <div className="col-md-3 box">
                <h2>Characters</h2>
                <div className="alert alert-info" role="alert" style={{ backgroundColor: '#e9ecef', padding: '20px' }}>
                        <h4 className="alert-heading">Instructions</h4>
                        <p>Review the visual descriptions and update anything you want. <br />AI made creative choices on many visual aspects that weren't explicit in the script.
                            <br />Generate images for your main characters. The AI will try to maintain visual consistency through the different images in the video. </p>
                    </div>
                    {characters.map((character, index) => (
                        <form id={`description-form-character-${index}`} key={index} onSubmit={(e) => e.preventDefault()}>
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5>{character.name}</h5>
                                    {character.asset && character.asset.url ? (
                                        <img 
                                            src={character.tempUrl} 
                                            alt={character.name} 
                                            className="img-fluid mb-3"
                                            key={character.tempUrl} // Force re-render when URL changes
                                        />
                                    ) : (
                                        <div className="img-fluid mb-3" style={{ 
                                            backgroundColor: '#e9ecef', 
                                            height: '200px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            border: '2px dashed #6c757d',
                                            borderRadius: '4px'
                                        }}>
                                            <div style={{ textAlign: 'center', color: '#6c757d' }}>
                                                {loadingStates[index] ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                                                        <p className="mt-2">Generating image...</p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p>No image yet</p>
                                                        <small>Generate an image below</small>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            value={character.description}
                                            onChange={(e) => handleDescriptionChange(index, e.target.value, 'character')}
                                            className="form-control"
                                            required
                                        ></textarea>
                                    </div>
                                    {changedDescriptions.character && changedDescriptions.character[index] && (
                                        <button
                                            type="button"
                                            className="btn btn-success mb-2 w-100"
                                            onClick={() => {
                                                handleSaveDescriptionClick(character, index, 'character');
                                                setChangedDescriptions({
                                                    ...changedDescriptions,
                                                    character: {
                                                        ...changedDescriptions.character,
                                                        [index]: false
                                                    }
                                                });
                                            }}
                                        >
                                            Save Changes
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-primary w-100"
                                        onClick={() => generateCharacterImage(character, index)}
                                        disabled={loadingStates[index]}
                                    >
                                        {loadingStates[index] ? 'Generating...' : 
                                         (changedDescriptions.character?.[index] ? 'Save & Generate (1 credit)' : 'Regenerate Image (1 credit)')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    ))}
                </div>
            </div>
        </div>
    );
}
